import Vue from 'vue'
import ProductSlider from '@/product/components/productSlider/ProductSlider.vue'
import AddonSection from '@/product/components/addonOptions/AddonSection.vue'
import ProductDetails from '@/product/components/ProductDetails.vue'
import GeneralInfo from '@/product/components/GeneralInfo.vue'
import ProductOptions from '@/product/components/ProductOptions.vue'
import AddToCartNow from '@/product/components/AddToCartNow.vue'
import RequestRestock from '@/product/components/RequestRestock.vue'
import ShippingOptions from '@/product/components/ShippingOptions.vue'
import AdditionalOptions from '@/product/components/additionalOptions/AdditionalOptions.vue'
import PrintingOptions from '@/product/components/printingOptions/PrintingOptions.vue'
import EstimateInvoice from '@/product/components/EstimateInvoice.vue'
import TagContainer from '@/product/components/TagContainer.vue'
import Billing from '@/product/components/Billing.vue'
import ProductPrecaution from '@/product/components/ProductPrecaution.vue'
import Services from '@/product/services'
import FAQ from '@/common/components/faq/FAQ.vue'
import ProductRelatedCategories from '@/product/components/ProductRelatedCategories.vue'

import { ProductState } from '@/product/types'
import { ProductDetailShippingEnum, QueryProductByPrintableSkuArgs } from '@/types/graphqlQuery'
import { Faq } from '@/types/common'
import { faqs } from '@/product/data/faqs'
import { SHIPPING_OPTIONS } from '@/product/const'

export default Vue.extend({
  components: {
    ProductSlider,
    GeneralInfo,
    ProductOptions,
    AddToCartNow,
    RequestRestock,
    ShippingOptions,
    AdditionalOptions,
    PrintingOptions,
    EstimateInvoice,
    AddonSection,
    ProductDetails,
    TagContainer,
    Billing,
    ProductPrecaution,
    FAQ,
    ProductRelatedCategories
  },
  computed: {
    storeState(): ProductState {
      return this.$store.state
    },
    printableSkuId(): string {
      return this.storeState.productDetail.printableSkuId
    },
    hasAddonProducts(): boolean {
      return this.storeState.productDetail.product.hasAddonProducts
    },
    hasAdditionalOptions(): boolean {
      return this.storeState.productDetail.product.hasOptions
    },
    fastShippingProductId(): string | null {
      return this.storeState.productDetail.product.fastShippingProductId
    },
    normalShippingProductId(): string | null {
      return this.storeState.productDetail.product.normalProductId
    },
    supportFastShipping(): boolean {
      return !!this.normalShippingProductId || !!this.fastShippingProductId
    },
    isFastShippingSelected(): boolean {
      return this.$store.getters.isFastShippingSelected
    },
    isDisablePlainGoodForFastShipping(): boolean {
      return this.isFastShippingSelected && !this.storeState.productDetail.product.plainGoodFastShipping
    },
    sizeInputQuery(): string {
      return this.$store.getters.sizeInputQuery
    },
    productQueries(): QueryProductByPrintableSkuArgs {
      return {
        id: this.storeState.productDetail.product.id,
        firstOptionId: this.storeState.estimate.firstSelectedOption?.id,
        skuColorPatternId: this.storeState.estimate.selectedColorPattern.id,
        skuEditPatternId: this.storeState.estimate.selectedEditPattern.id,
        sizes: this.sizeInputQuery,
        addonProductId: this.storeState.addonProduct?.selectedAddonProductId,
        addonFirstOptionId: this.storeState.addonProduct?.selectedAddonFirstOptionId,
        prefectureId: this.storeState.prefectureId,
        optionIds: this.storeState.selectedAdditionalOptionIds
      }
    },
    faqs(): Faq[] {
      return faqs
    }
  },
  methods: {
    updateRouteQueries() {
      const queries = {
        ...this.$route.query,
        psi: this.storeState.productDetail.printableSkuId,
        quantities: this.sizeInputQuery,
        addonPsi: this.storeState.estimate.addonPrintableSkuId || this.storeState.addonProduct?.selectedAddonProductId || '',
        designVersion: this.storeState.stockLibraryDesign.userDesignVersionUuid || '',
        optionIds: this.storeState.selectedAdditionalOptionIds.join(',')
      }
      this.$router.replace({
        path: this.$route.path.replace(
          this.$route.params.id,
          this.storeState.estimate.productId
        ),
        query: queries
      })
    },
    async onChangeShippingOption(isFastShipping: boolean, shippingOption: SHIPPING_OPTIONS) {
      const selectedProductId = isFastShipping
        ? this.fastShippingProductId!
        : this.normalShippingProductId!
      this.$store.commit('setProductLoading', true)
      const shippingVariable = isFastShipping && shippingOption === SHIPPING_OPTIONS.SAME_DAY ? ProductDetailShippingEnum.SameDay : undefined
      const data = await Services.getProductDetailData({
        id: selectedProductId,
        shipping: shippingVariable,
        sizes: this.$store.getters.sizeInputQuery
      })
        .catch(() => { this.$store.commit('setServerError', true) })
        .finally(() => { this.$store.commit('setProductLoading', false) })
      if (data) {
        await this.$store.dispatch('initProductDetail', data.productByPrintableSku)
        this.$router.replace({
          path: this.$route.path.replace(
            this.$route.params.id,
            this.storeState.estimate.productId
          )
        })
      }
    },
    async onChangeProductPsi({ loadImageView = false }) {
      if (loadImageView) this.$store.commit('setImageLoading', true)
      await this.$store.dispatch('getProductSkuData', this.productQueries)
      this.$store.commit('setImageLoading', false)
      this.updateRouteQueries()
    },
    async onChangePrefecture(prefectureId: string) {
      this.$store.commit('setPrefectureId', prefectureId)
      await this.$store.dispatch('getDeliveryDates', {
        ...this.productQueries,
        prefectureId
      })
    },
    async onChangeEstimateInputs() {
      await this.$store.dispatch('updateEstimateInputs', this.productQueries)
      this.updateRouteQueries()
    },
    removeStockLibraryData() {
      this.updateRouteQueries()
    },
    onUpdateAddonProduct() {
      this.onChangeEstimateInputs()
    }
  }
})
